import { ClockIcon } from '@radix-ui/react-icons';
import { ClassValue } from 'clsx';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import cn from '../../app/utils/cn';
import { parseFrom12hToTimeModel } from '@clickersphere/core/utils/parseTime';
import { Button } from './Button';
import Divider from './Divider';
import { Popover } from './Popover';
import ScrollArea from './ScrollArea';

interface ITimePickerProps {
  onSelect: (value: string) => void;
  onCancel: () => void;
  toggleVisibility?: () => void;
  open?: boolean;
  className?: ClassValue;
  value?: string;
  disabled?: boolean;
}

export default function TimePicker({
  className,
  value,
  open,
  disabled,
  onSelect,
  onCancel,
  toggleVisibility,
}: ITimePickerProps) {
  const { t } = useTranslation();
  const [hour, setHour] = useState<string>();
  const [minute, setMinute] = useState<string>();
  const [period, setPeriod] = useState<'AM' | 'PM'>();
  const hourRefs = useRef<{ [key: string]: HTMLButtonElement | null }>({});
  const minuteRefs = useRef<{ [key: string]: HTMLButtonElement | null }>({});

  const getHourOptions = (): string[] =>
    Array.from({ length: 12 }, (_, i) => (i + 1).toString().padStart(2, '0'));

  const getMinuteOptions = (): string[] =>
    Array.from({ length: 12 }, (_, i) => (i * 5).toString().padStart(2, '0'));

  function handleApply() {
    if (!hour || !minute || !period) return;
    onSelect(`${hour}:${minute} ${period}`);
    toggleVisibility?.();
  }

  useEffect(() => {
    if (value) {
      const { hour, minute, period } = parseFrom12hToTimeModel(value);
      setHour(hour);
      setMinute(minute);
      setPeriod(period);
    }
  }, [value]);

  useLayoutEffect(() => {
    if (!open) return;
    const id = requestAnimationFrame(() => {
      if (hour && hourRefs.current[hour]) {
        hourRefs.current[hour]?.scrollIntoView({
          block: 'center',
          behavior: 'smooth',
        });
      }
      if (minute && minuteRefs.current[minute]) {
        minuteRefs.current[minute]?.scrollIntoView({
          block: 'center',
          behavior: 'smooth',
        });
      }
    });
    return () => cancelAnimationFrame(id);
  }, [open, hour, minute]);

  const optionClass =
    'text-text-primary hover:bg-hover-medium w-full rounded-xl px-2 py-1 transition-colors';
  const selectedOptionClass =
    'text-white-0 bg-green-600 hover:bg-green-700 dark:bg-green-500 dark:hover:bg-green-600 shadow';

  return (
    <Popover.Root open={open}>
      <Popover.Trigger
        onClick={toggleVisibility}
        className={className}
        disabled={disabled}
        aria-label={t('register.timePicker.triggerLabel')}
      >
        <ClockIcon
          className={cn(
            'text-text-tertiary',
            disabled && 'text-text-disabled cursor-default',
          )}
        />
      </Popover.Trigger>
      <Popover.Content className="bg-surface-modal flex gap-2 py-3">
        <div className="flex gap-2">
          <ScrollArea className="h-[225px]">
            <ul className="flex flex-col gap-1">
              {getHourOptions().map((item) => (
                <li key={item}>
                  <button
                    className={cn(
                      optionClass,
                      item === hour && selectedOptionClass,
                    )}
                    ref={(element) => {
                      hourRefs.current[item] = element;
                    }}
                    type="button"
                    onClick={() => setHour(item)}
                  >
                    {item}
                  </button>
                </li>
              ))}
            </ul>
          </ScrollArea>

          <ScrollArea className="h-[225px]">
            <ul className="flex flex-col gap-1">
              {getMinuteOptions().map((item) => (
                <li key={item}>
                  <button
                    className={cn(
                      optionClass,
                      item === minute && selectedOptionClass,
                    )}
                    ref={(element) => {
                      minuteRefs.current[item] = element;
                    }}
                    type="button"
                    onClick={() => setMinute(item)}
                  >
                    {item}
                  </button>
                </li>
              ))}
            </ul>
          </ScrollArea>

          <ul className="flex flex-col gap-1">
            <li>
              <button
                type="button"
                className={cn(
                  optionClass,
                  period === 'AM' && selectedOptionClass,
                )}
                onClick={() => setPeriod('AM')}
              >
                AM
              </button>
            </li>
            <li>
              <button
                type="button"
                className={cn(
                  optionClass,
                  period === 'PM' && selectedOptionClass,
                )}
                onClick={() => setPeriod('PM')}
              >
                PM
              </button>
            </li>
          </ul>
        </div>

        <Divider vertical />

        <div className="space-y-2">
          <Button disabled={!hour || !minute || !period} onClick={handleApply}>
            {t('register.timePicker.apply')}
          </Button>
          <Button color="ghost" onClick={onCancel}>
            {t('register.timePicker.cancel')}
          </Button>
        </div>
      </Popover.Content>
    </Popover.Root>
  );
}
